import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "../firebase/firebase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./authProvider";

function Login() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verifyEmail, setVerifyEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setIsAuthenticated, setIsOtpVerified } = useAuth();
  const BACKEND_URL = "https://us-central1-ahsanali-web.cloudfunctions.net/api";

  const navigation = useNavigate();

  useEffect(() => {
    const setSessionPersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (error) {
        console.error("Error setting persistence:", error);
      }
    };

    setSessionPersistence();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("User is signed in:", user);
      } else {
        console.log("No user is signed in.");
      }
    });

    return () => unsubscribe();
  }, []);

  const handleStepOne = async () => {
    setError("");
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    try {
      setIsLoading(true);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      console.log("User signed in:", user);
      setStep(2);
    } catch (error) {
      console.error("Login error:", error);
      setError(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepTwo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: verifyEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send OTP");
      }

      setStep(3);
    } catch (error) {
      console.error("Email verification error:", error);
      setError(error.message || "Something went wrong while sending OTP.");
    }
  };

  const handleStepThree = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: verifyEmail,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsOtpVerified(true);
        navigation("/");
      } else {
        setIsOtpVerified(false);
        setError(data.error || "Invalid OTP");
      }
    } catch (error) {
      setIsOtpVerified(false);
      setError("Something went wrong. Please try again.");
    }
  };

  const resendOtp = async () => {
    setError("");
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: verifyEmail }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to send OTP");
      }

      console.log("OTP Sent:", data.message);
    } catch (error) {
      console.error("Resend OTP error:", error);
      setError(error.message || "Failed to resend OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-300 flex h-screen w-screen justify-center items-center">
      <div className="w-[25%] mx-auto p-6 bg-white shadow-md rounded-md">
        {step === 1 && (
          <>
            <h2 className="text-2xl font-bold mb-4">
              Enter Email and Password
            </h2>
            {error && <div className="text-red-500 mb-4">{error}</div>}
            <div className="mb-4">
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="email"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="password"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                placeholder="Enter your password"
                required
              />
            </div>
            <button
              type="submit"
              className={`w-full ${
                isLoading ? "bg-gray-400" : " bg-customblue  hover:bg-blue-700"
              } text-white px-4 py-2 rounded-md`}
              disabled={isLoading}
              onClick={handleStepOne}
            >
              {isLoading ? "Loading..." : "Next"}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-2xl font-bold mb-4">Enter Email</h2>
            {error && <div className="text-red-500 mb-4">{error}</div>}

            <div className="mb-4">
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="verifyEmail"
              >
                Email
              </label>
              <input
                type="email"
                id="verifyEmail"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                placeholder="Enter your Email"
                required
              />
            </div>
            <button
              type="submit"
              className={`w-full ${
                isLoading ? "bg-gray-400" : " bg-customblue  hover:bg-blue-700"
              } text-white px-4 py-2 rounded-md`}
              disabled={isLoading}
              onClick={handleStepTwo}
            >
              {isLoading ? "Loading..." : "Next"}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="text-2xl font-bold mb-4"> Verify OTP</h2>
            {error && <div className="text-red-500 mb-4">{error}</div>}

            <div className="mb-4">
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                placeholder="Enter the OTP sent to your Email Address"
                maxLength={6}
                required
              />
            </div>
            <div className="flex mb-4">
              <button
                onClick={resendOtp}
                type="button"
                className="flex text-sm w-ful justify-start items-start"
              >
                Resend OTP
              </button>
            </div>

            <button
              type="submit"
              className={`w-full ${
                isLoading ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
              } text-white px-4 py-2 rounded-md`}
              disabled={isLoading}
              onClick={handleStepThree}
            >
              {isLoading ? "Loading..." : "Verify"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
